import React, { useEffect, useState, useCallback } from "react";
import { Modal, Button } from "react-bootstrap";
import Multiselect from "multiselect-react-dropdown";
import DataTable from "react-data-table-component";
import { MdOutlineAssignmentReturned, MdOutlineDelete } from "react-icons/md";
import { notifySuccess, notifyError } from "../../Services/Notifier";
import { WidgetService } from "./Services/WidgetService";

/* ================= INITIAL FORM ================= */
const initialForm = {
    Title: "",
    ArTitle: "",
    IsPublished: false,
};

const WidgetModal = ({ show, onClose, widgetId, onSaved }) => {
    const isNew = widgetId === null;

    /* ================= STATE ================= */
    const [form, setForm] = useState(initialForm);

    const [categories, setCategories] = useState([]);
    const [selectedCategories, setSelectedCategories] = useState([]);

    // SOURCE
    const [assignedAttrs, setAssignedAttrs] = useState([]);
    const [unassignedAttrs, setUnassignedAttrs] = useState([]);

    // FILTERED
    const [filteredAssignedAttrs, setFilteredAssignedAttrs] = useState([]);
    const [filteredUnassignedAttrs, setFilteredUnassignedAttrs] = useState([]);

    const [showAssignModal, setShowAssignModal] = useState(false);
    const [saving, setSaving] = useState(false);

    /* ================= HELPERS ================= */
    const update = (key, value) =>
        setForm(prev => ({ ...prev, [key]: value }));

    const resetForm = () => {
        setForm(initialForm);
        setCategories([]);
        setSelectedCategories([]);
        setAssignedAttrs([]);
        setUnassignedAttrs([]);
        setFilteredAssignedAttrs([]);
        setFilteredUnassignedAttrs([]);
        setShowAssignModal(false);
        setSaving(false);
    };

    /* ================= LOAD DATA ================= */
    const loadData = useCallback(async () => {
        try {
            const cats = await WidgetService.getCategories();
            setCategories(cats);

            if (!isNew) {
                const widget = await WidgetService.getById(widgetId);

                setForm({
                    Title: widget.Title,
                    ArTitle: widget.ArTitle ?? "",
                    IsPublished: widget.IsPublished,
                });

                const assignedCats =
                    await WidgetService.getAssignedCategories(widgetId);
                setSelectedCategories(assignedCats);

                await reloadAttributes(widgetId);
            } else {
                resetForm();
                setCategories(cats);
            }
        } catch (ex) {
            console.error(ex);
            notifyError("Failed to load widget data");
        }
    }, [widgetId, isNew]);

    const reloadAttributes = async (id) => {
        const [assigned, unassigned] = await Promise.all([
            WidgetService.getAssignedAttributes(id),
            WidgetService.getUnAssignedAttributes(id),
        ]);

        setAssignedAttrs(assigned);
        setFilteredAssignedAttrs(assigned);

        setUnassignedAttrs(unassigned);
        setFilteredUnassignedAttrs(unassigned);
    };

    /* ================= SEARCH ================= */
    const searchAssigned = (e) => {
        const val = e.target.value.toLowerCase();
        setFilteredAssignedAttrs(
            assignedAttrs.filter(a =>
                a.AttributeValueName.toLowerCase().includes(val)
            )
        );
    };

    const searchUnassigned = (e) => {
        const val = e.target.value.toLowerCase();
        setFilteredUnassignedAttrs(
            unassignedAttrs.filter(a =>
                a.AttributeValueName.toLowerCase().includes(val)
            )
        );
    };

    /* ================= ATTRIBUTES ================= */
    const assignAttribute = async (row) => {
        try {
            await WidgetService.assignAttribute(widgetId, row.AttributeValueId);
            notifySuccess("Attribute assigned");
            await reloadAttributes(widgetId);
            setShowAssignModal(false);
        } catch {
            notifyError("Assign failed");
        }
    };

    const unassignAttribute = async (attributeValueId) => {
        try {
            await WidgetService.unassignAttribute(widgetId, attributeValueId);
            notifySuccess("Attribute removed");
            await reloadAttributes(widgetId);
        } catch {
            notifyError("Unassign failed");
        }
    };

    /* ================= SAVE ================= */
    const save = async () => {
        if (!form.Title)
            return notifyError("Widget title required");

        if (!selectedCategories.length)
            return notifyError("Please assign at least one category");

        try {
            setSaving(true);

            const id = isNew
                ? await WidgetService.create(form)
                : (await WidgetService.update(widgetId, form), widgetId);

            await WidgetService.assignCategories(id, selectedCategories);

            notifySuccess(isNew ? "Widget added" : "Widget updated");
            onSaved();
            onClose();
        } catch (ex) {
            notifyError(typeof ex === "string" ? ex : ex?.message || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    /* ================= EFFECT ================= */
    useEffect(() => {
        if (show) loadData();
        else resetForm();
    }, [show]);

    if (!show) return null;

    /* ================= UI ================= */
    return (
        <Modal size="xl" show={show} onHide={onClose} centered backdrop="static">
            <Modal.Header closeButton>
                <Modal.Title>
                    {isNew ? "Add Widget" : `Manage ${form.Title}`}
                </Modal.Title>
            </Modal.Header>

            <Modal.Body>
                {/* ---------- TITLES ---------- */}
                <div className="row mb-3">
                    <div className="col-md-6">
                        <label className="form-label">Title (EN) *</label>
                        <input
                            className="form-control"
                            value={form.Title}
                            onChange={(e) => update("Title", e.target.value)}
                        />
                    </div>

                    <div className="col-md-6">
                        <label className="form-label">Title (AR)</label>
                        <input
                            className="form-control text-end"
                            dir="rtl"
                            value={form.ArTitle}
                            onChange={(e) => update("ArTitle", e.target.value)}
                        />
                    </div>
                </div>

                {/* ---------- CATEGORIES ---------- */}
                <label className="form-label">Categories</label>
                <Multiselect
                    options={categories}
                    selectedValues={selectedCategories}
                    displayValue="CategoryName"
                    onSelect={setSelectedCategories}
                    onRemove={setSelectedCategories}
                />

                {/* ---------- ATTRIBUTES ---------- */}
                {!isNew && (
                    <div className="card mt-4">
                        <div className="card-header d-flex justify-content-between align-items-center">
                            <h5 className="mb-0">Assigned Attributes</h5>
                            <button
                                className="btn btn-primary"
                                onClick={() => setShowAssignModal(true)}
                            >
                                Assign Attribute
                            </button>
                        </div>

                        <div className="card-body">
                            <div className="row mb-3">
                                <div className="col-8"></div>
                                <div className="col-4">
                                    <input
                                        className="form-control form-control-sm"
                                        placeholder="Search attribute"
                                        onChange={searchAssigned}
                                    />
                                </div>
                            </div>

                            <DataTable
                                columns={[
                                    {
                                        name: "Attribute",
                                        selector: r => r.AttributeValueName,
                                        sortable: true,
                                    },
                                    {
                                        name: "Action",
                                        cell: r => (
                                            <button
                                                className="btn btn-outline-danger btn-sm"
                                                onClick={() =>
                                                    unassignAttribute(r.AttributeValueId)
                                                }
                                            >
                                                <MdOutlineDelete />
                                            </button>
                                        ),
                                    },
                                ]}
                                data={filteredAssignedAttrs}
                                pagination
                            />
                        </div>
                    </div>
                )}

                {/* ---------- ASSIGN MODAL ---------- */}
                <Modal
                    dialogClassName="w-max"
                    show={showAssignModal}
                    onHide={() => setShowAssignModal(false)}
                    centered
                >
                    <Modal.Header closeButton>
                        <Modal.Title>Assign Attribute</Modal.Title>
                    </Modal.Header>

                    <Modal.Body>
                        <div className="row mb-3">
                            <div className="col-8"></div>
                            <div className="col-4">
                                <input
                                    className="form-control form-control-sm"
                                    placeholder="Search attribute"
                                    onChange={searchUnassigned}
                                />
                            </div>
                        </div>

                        <DataTable
                            columns={[
                                {
                                    name: "Attribute",
                                    selector: r => r.AttributeValueName,
                                    sortable: true,
                                },
                                {
                                    name: "Action",
                                    cell: r => (
                                        <button
                                            className="btn btn-outline-secondary btn-sm"
                                            onClick={() => assignAttribute(r)}
                                        >
                                            <MdOutlineAssignmentReturned />
                                        </button>
                                    ),
                                },
                            ]}
                            data={filteredUnassignedAttrs}
                            pagination
                        />
                    </Modal.Body>

                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowAssignModal(false)}>
                            Close
                        </Button>
                    </Modal.Footer>
                </Modal>
            </Modal.Body>

            <Modal.Footer>
                <Button variant="secondary" onClick={onClose}>
                    Cancel
                </Button>
                <Button variant="primary" disabled={saving} onClick={save}>
                    {saving ? "Saving..." : "Save"}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default WidgetModal;
